// CSRF client utility for secure form submissions
let csrfToken: string | null = null;
let tokenExpiry: Date | null = null;

// Get CSRF token from server
export async function getCsrfToken(): Promise<string> {
  // Check if we have a valid cached token
  if (csrfToken && tokenExpiry && new Date() < tokenExpiry) {
    return csrfToken;
  }

  try {
    const response = await fetch('/api/csrf-token', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }

    const data = await response.json();
    csrfToken = data.csrfToken;
    tokenExpiry = new Date(data.expires);
    
    return csrfToken!;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw new Error('Unable to get CSRF token');
  }
}

// Enhanced fetch with automatic CSRF token inclusion
export async function secureApiRequest(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  // Get CSRF token for state-changing requests
  if (options.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) {
    const token = await getCsrfToken();
    
    // Add CSRF token to headers
    const headers = new Headers(options.headers);
    headers.set('X-CSRF-Token', token);
    
    // Also add to body if it's form data
    if (options.body instanceof FormData) {
      (options.body as FormData).set('_csrf', token);
    } else if (options.body && typeof options.body === 'string') {
      try {
        const bodyData = JSON.parse(options.body);
        bodyData._csrf = token;
        options.body = JSON.stringify(bodyData);
      } catch (e) {
        // If it's not JSON, add as form data
        if (options.body.includes('=')) {
          options.body += `&_csrf=${encodeURIComponent(token)}`;
        }
      }
    }
    
    options.headers = headers;
  }

  // Make the request with credentials
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  });

  // If CSRF token is invalid, clear cache and retry once
  if (response.status === 403) {
    // Clone the response before reading the body so the original remains usable
    const clonedResponse = response.clone();
    const errorData = await clonedResponse.json().catch(() => ({}));
    if (errorData.code === 'CSRF_TOKEN_INVALID' || errorData.code === 'CSRF_TOKEN_MISSING') {
      // Clear token cache
      csrfToken = null;
      tokenExpiry = null;
      
      // Retry the request with fresh token
      if (options.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) {
        const freshToken = await getCsrfToken();
        const headers = new Headers(options.headers);
        headers.set('X-CSRF-Token', freshToken);
        
        if (options.body instanceof FormData) {
          (options.body as FormData).set('_csrf', freshToken);
        } else if (options.body && typeof options.body === 'string') {
          try {
            const bodyData = JSON.parse(options.body);
            bodyData._csrf = freshToken;
            options.body = JSON.stringify(bodyData);
          } catch (e) {
            // Handle form-encoded data
            if (options.body.includes('=')) {
              options.body = options.body.replace(/&_csrf=[^&]*/, '') + `&_csrf=${encodeURIComponent(freshToken)}`;
            }
          }
        }
        
        options.headers = headers;
        return fetch(url, { ...options, credentials: 'include' });
      }
    }
  }

  return response;
}

// Clear CSRF token cache (useful for logout)
export function clearCsrfToken(): void {
  csrfToken = null;
  tokenExpiry = null;
}

// Check if CSRF token is available and valid
export function hasCsrfToken(): boolean {
  return !!(csrfToken && tokenExpiry && new Date() < tokenExpiry);
}